// Bilingual patient-message templates (R-016 — the healthcheck's comprehension-
// safety P0). Blueprint §3 recorded ruling: staff UI stays English-only, but
// patient-facing messages follow the PATIENT, English or Kiswahili, chosen per
// send. Ported from the legacy WhatsApp/SMS template sets (v20 whatsappPatient +
// draftMessage), now with a natural-clinical Kiswahili twin for each template.
//
// These are PURE builders: a template is `(vars) => string` per language, so the
// composer can fill placeholders, unit tests can assert both languages render,
// and nothing here touches the DOM. The composer (views/workspace.js) is the
// only caller that puts the result on screen — and it esc()s the filled text
// before it ever reaches innerHTML.
//
// LANGUAGE SELECTION (recorded decision): a per-PATIENT language preference wins
// when the record carries one (comms_language / language / comms_consent.language
// — forward-compatible field names). The live consent app does NOT yet persist a
// patient language, so until it does we fall back to the NURSE's last choice
// (localStorage, per-user). When the consent handshake starts recording language,
// pickLanguage() already reads it — no code change, the field just starts arriving.

export const LANGS = Object.freeze(['en', 'sw']);
export const LANG_LABEL = Object.freeze({ en: 'English', sw: 'Kiswahili' });

export const MSG_LANG_KEY_PREFIX = 'tumaini_msg_lang_'; // per-user last-choice

/**
 * A message variable bag. Every field is optional; missing values fall back to
 * neutral, non-lying placeholders (never a raw "undefined" into a patient text).
 * @typedef {Object} MsgVars
 * @property {string} [name] patient FIRST name (composer passes the first token)
 * @property {string} [ref] case reference number
 * @property {string} [nextDate] next appointment / refill date, already formatted
 * @property {string} [programme] programme display name
 * @property {string} [nurse] the sending nurse's name (WhatsApp/SMS signature)
 */

/** Safe first-name for a greeting (EN "there" / SW "rafiki" as the neutral fill). */
function who(vars, fallback) {
  const n = vars && vars.name ? String(vars.name).trim() : '';
  return n || fallback;
}

/**
 * The four core patient templates the nurse daily loop needs (Task C scope:
 * welcome, refill reminder, day-15 adherence check, missed appointment). Each
 * carries an EN and a SW builder. Kept deliberately short — SMS-length, warm,
 * clinically accurate, no jargon.
 * @type {ReadonlyArray<{id: string, label: {en: string, sw: string}, build: {en: (v: MsgVars) => string, sw: (v: MsgVars) => string}}>}
 */
export const MESSAGE_TEMPLATES = Object.freeze([
  Object.freeze({
    id: 'welcome',
    label: Object.freeze({ en: 'Welcome', sw: 'Karibu' }),
    build: Object.freeze({
      en: (v) =>
        `Hello ${who(v, 'there')}, welcome to the ${v.programme || 'Tumaini'} programme. ` +
        `I am ${v.nurse || 'your care team'}, your patient-support nurse. ` +
        `Feel free to reach out on this number any time you have a question.`,
      sw: (v) =>
        `Habari ${who(v, 'rafiki')}, karibu katika programu ya ${v.programme || 'Tumaini'}. ` +
        `Mimi ni ${v.nurse || 'timu yako ya matibabu'}, muuguzi wako. ` +
        `Uko huru kuwasiliana nami kupitia nambari hii wakati wowote ukiwa na swali.`,
    }),
  }),
  Object.freeze({
    id: 'refill',
    label: Object.freeze({ en: 'Refill reminder', sw: 'Kumbusho la dawa' }),
    build: Object.freeze({
      en: (v) =>
        `Hi ${who(v, 'there')}, your medication refill is coming up${v.nextDate ? ' on ' + v.nextDate : ' soon'}. ` +
        `Please make sure your SHA premium is up to date so we can process the approval without delays. ` +
        `Reply here with any questions.`,
      sw: (v) =>
        `Habari ${who(v, 'rafiki')}, muda wa kuongeza dawa yako unakaribia${v.nextDate ? ', tarehe ' + v.nextDate : ' hivi karibuni'}. ` +
        `Tafadhali hakikisha malipo yako ya SHA yako sawa ili tuidhinishe bila kuchelewa. ` +
        `Jibu hapa ukiwa na swali lolote.`,
    }),
  }),
  Object.freeze({
    id: 'adherence15',
    label: Object.freeze({ en: 'Day-15 adherence check', sw: 'Ukaguzi wa siku 15' }),
    build: Object.freeze({
      en: (v) =>
        `Hi ${who(v, 'there')}, it is now about two weeks since you started treatment. ` +
        `Are you able to take your medicine every day as prescribed? ` +
        `Please tell me about any side effects or difficulties — we are here to help.`,
      sw: (v) =>
        `Habari ${who(v, 'rafiki')}, sasa ni takriban wiki mbili tangu uanze matibabu. ` +
        `Je, unaweza kutumia dawa yako kila siku kama ulivyoelekezwa? ` +
        `Tafadhali nieleze kuhusu madhara yoyote au changamoto — tuko hapa kukusaidia.`,
    }),
  }),
  Object.freeze({
    id: 'missed',
    label: Object.freeze({ en: 'Missed appointment', sw: 'Miadi iliyokoswa' }),
    build: Object.freeze({
      en: (v) =>
        `Hi ${who(v, 'there')}, we tried to reach you for your scheduled check-in but could not get through. ` +
        `Could you let me know a good time to call? ` +
        `It is important that we stay in touch about your treatment.`,
      sw: (v) =>
        `Habari ${who(v, 'rafiki')}, tulijaribu kukupigia simu kwa miadi yako lakini hatukufanikiwa. ` +
        `Tafadhali nijulishe wakati mzuri wa kukupigia. ` +
        `Ni muhimu tuendelee kuwasiliana kuhusu matibabu yako.`,
    }),
  }),
]);

/**
 * Look up a template by id (or null).
 * @param {string} id
 * @returns {(typeof MESSAGE_TEMPLATES)[number]|null}
 */
export function templateById(id) {
  return MESSAGE_TEMPLATES.find((t) => t.id === id) || null;
}

/**
 * Render a template in a language with the variables filled. Unknown language
 * falls back to English; unknown template id returns ''. Pure.
 * @param {string} id template id
 * @param {'en'|'sw'|string} lang
 * @param {MsgVars} [vars]
 * @returns {string}
 */
export function renderTemplate(id, lang, vars) {
  const t = templateById(id);
  if (!t) return '';
  const build = t.build[lang === 'sw' ? 'sw' : 'en'];
  return build(vars || {});
}

/**
 * The label for a template's chip in a given language (falls back to EN).
 * @param {string} id
 * @param {'en'|'sw'|string} lang
 * @returns {string}
 */
export function templateLabel(id, lang) {
  const t = templateById(id);
  if (!t) return '';
  return t.label[lang === 'sw' ? 'sw' : 'en'];
}

/**
 * Decide which language to open the composer in. A per-PATIENT preference wins
 * when the case row carries one (comms_language / language / comms_consent.language
 * — see the module header on why the field may be absent today); otherwise the
 * nurse's last per-user choice applies; otherwise English. Pure — the caller
 * passes the row and the stored last-choice so it tests without a store.
 * @param {any} caseRow the loaded case (may lack any language field)
 * @param {'en'|'sw'|string|null|undefined} lastChoice the nurse's stored last language
 * @returns {'en'|'sw'}
 */
export function pickLanguage(caseRow, lastChoice) {
  const cc = (caseRow && caseRow.comms_consent) || {};
  const pref = caseRow && (caseRow.comms_language || caseRow.language || cc.language);
  if (pref === 'sw' || pref === 'en') return pref; // per-patient preference wins
  return lastChoice === 'sw' ? 'sw' : 'en';
}

/**
 * Read the nurse's last language choice (per-user localStorage). Never throws.
 * @param {string|null|undefined} uid
 * @param {any} [storage]
 * @returns {'en'|'sw'}
 */
export function readLastLang(uid, storage) {
  try {
    const st = storage || /** @type {any} */ (globalThis).localStorage;
    const v = st.getItem(MSG_LANG_KEY_PREFIX + (uid || 'anon'));
    return v === 'sw' ? 'sw' : 'en';
  } catch {
    return 'en';
  }
}

/**
 * Persist the nurse's last language choice (per-user). Never throws.
 * @param {string|null|undefined} uid
 * @param {'en'|'sw'} lang
 * @param {any} [storage]
 * @returns {void}
 */
export function writeLastLang(uid, lang, storage) {
  try {
    const st = storage || /** @type {any} */ (globalThis).localStorage;
    st.setItem(MSG_LANG_KEY_PREFIX + (uid || 'anon'), lang === 'sw' ? 'sw' : 'en');
  } catch {
    /* storage unavailable — the in-session choice still applies */
  }
}

/**
 * Draft-edit guard (ports the v20.1 "don't silently discard nurse edits" rule to
 * the new bilingual toggle): a draft is DIRTY when the nurse has changed it away
 * from the text a template last generated. A language/template switch must never
 * silently overwrite a dirty draft — the composer keeps the text and offers an
 * explicit "replace" instead. Pure.
 * @param {string} current the textarea value now
 * @param {string} lastGenerated the text the composer last auto-filled ('' if none)
 * @returns {boolean} true when regenerating would DESTROY nurse edits
 */
export function draftIsDirty(current, lastGenerated) {
  const cur = String(current === null || current === undefined ? '' : current);
  if (!cur.trim()) return false; // empty draft — nothing to protect
  const gen = String(lastGenerated === null || lastGenerated === undefined ? '' : lastGenerated);
  return cur !== gen;
}
